// API client for admin operations
const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  // Auth relies solely on the httpOnly `session_token` cookie (sent via
  // credentials: 'include'). We never read tokens from localStorage (XSS-safe).
  return { 'Content-Type': 'application/json' };
};

const request = async (path, options = {}) => {
  const res = await fetch(`${API}${path}`, {
    credentials: 'include',
    headers: authHeaders(),
    ...options,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
};

// Public APIs
export const publicApi = {
  getPackages: (category) =>
    request(`/api/public/packages${category ? `?category=${category}` : ''}`),
  getServices: () => request('/api/public/services'),
  getGallery: () => request('/api/public/gallery'),
  getTestimonials: () => request('/api/public/testimonials'),
  getFaqs: () => request('/api/public/faqs'),
  getCategories: () => request('/api/public/categories'),
  getSettings: () => request('/api/public/settings'),
  getEvents: (category) =>
    request(`/api/public/events${category ? `?category=${category}` : ''}`),
  getEvent: (slug) => request(`/api/public/events/${slug}`),
  verifyEventCode: (slug, access_code) =>
    request(`/api/public/events/${slug}/verify`, {
      method: 'POST',
      body: JSON.stringify({ access_code }),
    }),
  getPageContent: (pageId) => request(`/api/public/page-content/${pageId}`),
  submitQuote: (data) =>
    request('/api/quote-requests', { method: 'POST', body: JSON.stringify(data) }),
  validatePromo: (code, service, upgrades) =>
    request('/api/promo/validate', {
      method: 'POST',
      body: JSON.stringify({ code, service, upgrades }),
    }),
};

// Admin APIs - generic CRUD helper
const crud = (resource) => ({
  list: () => request(`/api/admin/${resource}`),
  create: (data) =>
    request(`/api/admin/${resource}`, { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) =>
    request(`/api/admin/${resource}/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id) =>
    request(`/api/admin/${resource}/${id}`, { method: 'DELETE' }),
});

export const adminApi = {
  packages: crud('packages'),
  promoCodes: crud('promo-codes'),
  services: crud('services'),
  gallery: crud('gallery'),
  testimonials: crud('testimonials'),
  faqs: crud('faqs'),
  events: crud('events'),
  categories: {
    ...crud('categories'),
    reorder: (ids) =>
      request('/api/admin/categories/reorder', { method: 'PUT', body: JSON.stringify({ ids }) }),
  },
  getSettings: () => request('/api/admin/settings'),
  updateSettings: (data) =>
    request('/api/admin/settings', { method: 'PUT', body: JSON.stringify(data) }),
  getQuoteRequests: () => request('/api/admin/quote-requests'),
  updateQuoteStatus: (id, status) =>
    request(`/api/admin/quote-requests/${id}/status?status=${status}`, { method: 'PUT' }),
  deleteQuoteRequest: (id) =>
    request(`/api/admin/quote-requests/${id}`, { method: 'DELETE' }),
  getCloudinarySignature: (folder = 'maydenfilms/events', resourceType = 'image') =>
    request(`/api/admin/cloudinary/signature?folder=${encodeURIComponent(folder)}&resource_type=${resourceType}`),
  deleteCloudinaryAsset: (publicId, resourceType = 'image') =>
    request(
      `/api/admin/cloudinary/asset?public_id=${encodeURIComponent(publicId)}&resource_type=${resourceType}`,
      { method: 'DELETE' }
    ),
  getPageContent: (pageId) => request(`/api/admin/page-content/${pageId}`),
  updatePageContent: (pageId, content) =>
    request(`/api/admin/page-content/${pageId}`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    }),
};

// Upload a file directly to Cloudinary using a signed signature
/**
 * Downscale + re-encode large images in the browser BEFORE upload.
 * Modern phone/camera photos are 5–25 MB; uploading them raw is slow and can
 * stall. We cap the longest edge and re-encode as JPEG so uploads are fast and
 * stay well under Cloudinary limits. Best-effort: any failure returns the
 * original file unchanged (e.g. desktop browsers that can't decode HEIC).
 */
export const compressImage = (file, { maxDim = 2400, quality = 0.82 } = {}) =>
  new Promise((resolve) => {
    if (!file || !file.type || !file.type.startsWith('image/')) return resolve(file);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      try {
        const { width, height } = img;
        const scale = Math.min(1, maxDim / Math.max(width, height));
        const targetW = Math.round(width * scale);
        const targetH = Math.round(height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = targetW;
        canvas.height = targetH;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, targetW, targetH);
        canvas.toBlob(
          (blob) => {
            if (blob && blob.size < file.size) {
              const name = file.name.replace(/\.(heic|heif|png|webp|bmp|tiff?)$/i, '.jpg');
              resolve(new File([blob], name, { type: 'image/jpeg' }));
            } else {
              resolve(file);
            }
          },
          'image/jpeg',
          quality
        );
      } catch {
        resolve(file);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });

export const uploadToCloudinary = async (file, folder = 'maydenfilms/events', resourceType = 'image') => {
  const toUpload = resourceType === 'image' ? await compressImage(file) : file;
  const sig = await adminApi.getCloudinarySignature(folder, resourceType);
  const form = new FormData();
  form.append('file', toUpload);
  form.append('api_key', sig.api_key);
  form.append('timestamp', sig.timestamp);
  form.append('signature', sig.signature);
  form.append('folder', sig.folder);
  const endpoint = `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${resourceType}/upload`;

  // Guard against stalled requests so the UI never freezes indefinitely.
  const controller = new AbortController();
  const timeoutMs = resourceType === 'video' ? 240000 : 90000;
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(endpoint, { method: 'POST', body: form, signal: controller.signal });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error?.message || 'Upload failed');
    }
    const data = await res.json();
    return { url: data.secure_url, public_id: data.public_id };
  } catch (e) {
    if (e.name === 'AbortError') {
      throw new Error('Upload timed out — please check your connection and try again (or use a smaller file).');
    }
    throw e;
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Derive a JPG poster URL from a Cloudinary video URL.
 * Cloudinary auto-generates a thumbnail at any time offset just by
 * swapping the file extension. We also downscale to ~800w and use
 * f_auto/q_auto so the grid loads fast.
 *
 * Falls back to the original URL for non-Cloudinary videos.
 */
export const getVideoThumbnail = (video) => {
  if (!video) return '';
  if (video.thumbnail) return video.thumbnail;
  const url = video.url || '';
  if (!url.includes('/video/upload/')) return url;
  return url
    .replace('/video/upload/', '/video/upload/so_auto,w_800,c_fill,f_jpg,q_auto/')
    .replace(/\.(mp4|mov|webm|m4v|avi)(\?.*)?$/i, '.jpg$2');
};

/**
 * Return an optimised, resized Cloudinary image URL. This serves web-friendly
 * versions (never the full-resolution original) for fast, mobile-friendly loads.
 *   variant 'thumb' → square grid tile
 *   variant 'full'  → large lightbox view (capped, not the original)
 * Falls back to the raw URL for non-Cloudinary images.
 */
export const getImageUrl = (url, variant = 'thumb') => {
  if (!url || !url.includes('/image/upload/')) return url || '';
  const t =
    variant === 'full'
      ? 'c_limit,w_1600,h_1600,f_auto,q_auto'
      : 'c_fill,g_auto,w_700,h_700,f_auto,q_auto';
  return url.replace('/image/upload/', `/image/upload/${t}/`);
};
